import { Plus, Trash2, Webhook } from "lucide-react";
import { DiscordChannel, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  onChange: (v: GuildConfig["webhooks"]) => void;
};

export function WebhooksTab({ config, channels, onChange }: Props) {
  const cfg = config.webhooks;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });
  const text = channels.filter((ch) => ch.type === 0);

  return (
    <Section
      icon={Webhook}
      title="Webhooks / scheduled posts"
      description="Schedule repeated messages. Use either a Discord webhook URL or a channel (bot sends the message)."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
      actions={
        <Button
          size="sm"
          onClick={() =>
            update("jobs", [
              ...(cfg.jobs || []),
              {
                id: `wh_${Date.now()}`,
                enabled: true,
                name: "New scheduled post",
                url: "",
                channel_id: "",
                content: "",
                embed_title: "",
                embed_description: "",
                interval_minutes: 60,
              },
            ])
          }
        >
          <Plus className="h-4 w-4" />
          Add job
        </Button>
      }
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          <strong>No slash commands.</strong> Each job sends plain text or an embed on a timer—either via a{" "}
          <strong>webhook URL</strong> or by posting through the bot in a selected <strong>channel</strong>. Use for
          reminders, notices, or recurring announcements.
        </p>
      </div>

      <div className="space-y-3">
        {(cfg.jobs || []).map((job, idx) => (
          <div key={job.id || idx} className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={job.enabled}
                onCheckedChange={(v) => {
                  const jobs = [...cfg.jobs];
                  jobs[idx] = { ...job, enabled: v };
                  update("jobs", jobs);
                }}
              />
              <Input
                value={job.name}
                onChange={(e) => {
                  const jobs = [...cfg.jobs];
                  jobs[idx] = { ...job, name: e.target.value };
                  update("jobs", jobs);
                }}
              />
              <Button size="icon" variant="ghost" onClick={() => update("jobs", cfg.jobs.filter((_, i) => i !== idx))}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Webhook URL (optional)">
                <Input
                  value={job.url}
                  onChange={(e) => {
                    const jobs = [...cfg.jobs];
                    jobs[idx] = { ...job, url: e.target.value };
                    update("jobs", jobs);
                  }}
                  placeholder="https://discord.com/api/webhooks/..."
                />
              </Field>
              <Field label="Or channel">
                <ChannelPicker
                  channels={text}
                  value={job.channel_id}
                  onChange={(v) => {
                    const jobs = [...cfg.jobs];
                    jobs[idx] = { ...job, channel_id: v === "__none__" ? "" : v };
                    update("jobs", jobs);
                  }}
                />
              </Field>
              <Field label="Every X minutes">
                <Input
                  type="number"
                  min={5}
                  max={10080}
                  value={job.interval_minutes}
                  onChange={(e) => {
                    const jobs = [...cfg.jobs];
                    jobs[idx] = { ...job, interval_minutes: Math.max(5, Number(e.target.value) || 60) };
                    update("jobs", jobs);
                  }}
                />
              </Field>
            </div>
            <Field label="Content">
              <Textarea rows={2} value={job.content} onChange={(e) => {
                const jobs = [...cfg.jobs];
                jobs[idx] = { ...job, content: e.target.value };
                update("jobs", jobs);
              }} />
            </Field>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Embed title">
                <Input value={job.embed_title} onChange={(e) => {
                  const jobs = [...cfg.jobs];
                  jobs[idx] = { ...job, embed_title: e.target.value };
                  update("jobs", jobs);
                }} />
              </Field>
              <Field label="Embed description">
                <Textarea rows={2} value={job.embed_description} onChange={(e) => {
                  const jobs = [...cfg.jobs];
                  jobs[idx] = { ...job, embed_description: e.target.value };
                  update("jobs", jobs);
                }} />
              </Field>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}
